import React, { useEffect, useMemo } from 'react';
import { X, Loader2 } from 'lucide-react';
import {
  DataGrid,
  DataGridColumnHeader,
  getDetailGridConfig,
  applyDetailGridConfig,
  type ColumnDef,
  type PaginationState,
  type SortingState,
  type DetailGridColumn,
} from '@t-one-internal/design-system';
import { useDrillToDetail } from '../../hooks/useDrillToDetail';
import type { DrillToDetailContext } from '../../types/drillToDetail';

interface DrillToDetailModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Drill context with metric info and filters */
  context: DrillToDetailContext;
  /** Page size for pagination */
  pageSize?: number;
}

/**
 * Drill-to-Detail Modal
 *
 * Displays detailed underlying data when users click on chart segments or KPI cards.
 * Uses design-system DataGrid with server-side pagination and sorting.
 */
export const DrillToDetailModal: React.FC<DrillToDetailModalProps> = ({
  isOpen,
  onClose,
  context,
  pageSize = 100,
}) => {
  const {
    data,
    columns,
    isLoading,
    error,
    currentPage,
    totalPages,
    totalRows,
    hasMore,
    sortState,
    fetchPage,
    sortBy,
    reset,
    initialize,
  } = useDrillToDetail({ pageSize });

  // Initialize data when modal opens
  useEffect(() => {
    if (isOpen && context) {
      initialize(context);
    }
    return () => {
      if (!isOpen) {
        reset();
      }
    };
  }, [isOpen, context, initialize, reset]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Format cell value for display
  const formatCellValue = (value: unknown, column: DetailGridColumn): string => {
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const formatDate = (d: Date) => {
      const day = String(d.getDate()).padStart(2, '0');
      return `${day}-${months[d.getMonth()]}-${d.getFullYear()}`;
    };
    // Handle datetime/date types (V2 schema uses DATETIME/DATE, legacy uses TIMESTAMP/LONG)
    const dt = column.data_type;
    if (dt === 'DATETIME' || dt === 'DATE' || dt === 'TIMESTAMP' || dt === 'TIMESTAMPTZ' || dt === 'LONG') {
      // ISO string (e.g. "2025-11-19T06:15:01Z")
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        const date = new Date(value);
        if (!isNaN(date.getTime())) return formatDate(date);
      }
      // Numeric timestamp (milliseconds)
      const num = Number(value);
      if (!isNaN(num) && num > 1000000000000) {
        return formatDate(new Date(num));
      }
    }
    const str = String(value);
    // Format snake_case status/category values as Title Case (e.g. "in_progress" → "In Progress")
    if (typeof value === 'string' && /^[a-z]+(_[a-z]+)+$/.test(str)) {
      return str.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    }
    return str;
  };

  // Get centralized config and apply to columns
  // For report builder widgets (no metricKey), skip grid config customization
  const displayColumns = useMemo(() => {
    if (!context.metricKey) {
      return columns as DetailGridColumn[];
    }
    const config = getDetailGridConfig(context.metricKey);
    return applyDetailGridConfig(columns as DetailGridColumn[], config);
  }, [columns, context.metricKey]);

  // Convert columns to DataGrid ColumnDef format
  const gridColumns = useMemo((): ColumnDef<Record<string, unknown>>[] => {
    return displayColumns.map((col) => ({
      id: col.name,
      accessorKey: col.name,
      header: ({ column }) => (
        <DataGridColumnHeader column={column} title={col.label} />
      ),
      cell: ({ row }) => {
        const value = row.getValue(col.name);
        return <span>{formatCellValue(value, col)}</span>;
      },
      enableSorting: col.sortable,
    }));
  }, [displayColumns]);

  // Pagination state for DataGrid
  const pagination: PaginationState = useMemo(() => ({
    pageIndex: currentPage - 1, // DataGrid uses 0-indexed pages
    pageSize,
  }), [currentPage, pageSize]);

  // Sorting state for DataGrid
  const sorting: SortingState = useMemo(() => {
    if (!sortState) return [];
    return [{
      id: sortState.field,
      desc: sortState.direction === 'desc',
    }];
  }, [sortState]);

  // Handle pagination change
  const handlePaginationChange = (updater: PaginationState | ((old: PaginationState) => PaginationState)) => {
    const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
    const newPage = newPagination.pageIndex + 1; // Convert back to 1-indexed
    if (newPage !== currentPage) {
      fetchPage(newPage);
    }
  };

  // Handle sorting change
  const handleSortingChange = (updater: SortingState | ((old: SortingState) => SortingState)) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      // Only trigger if actually changed
      if (sortState?.field !== id || (sortState?.direction === 'desc') !== desc) {
        sortBy(id);
      }
    }
  };

  // Build subtitle from context
  const getSubtitle = (): string => {
    const parts: string[] = [];

    if (context.segmentLabel) {
      parts.push(context.segmentLabel);
    }

    if (context.segmentValue !== undefined) {
      parts.push(`(${context.segmentValue.toLocaleString()} records)`);
    }

    return parts.join(' ');
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '24px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '1200px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            backgroundColor: '#f9fafb',
          }}
        >
          <div>
            <h2
              style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                margin: 0,
              }}
            >
              {context.widgetTitle} - Detail View
            </h2>
            {getSubtitle() && (
              <p
                style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: '4px 0 0 0',
                }}
              >
                {getSubtitle()}
              </p>
            )}
            <p
              style={{
                fontSize: '13px',
                color: '#9ca3af',
                margin: '4px 0 0 0',
              }}
            >
              {totalRows > 0
                ? `Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(
                    currentPage * pageSize,
                    totalRows
                  )} of ${totalRows.toLocaleString()} records`
                : 'Loading...'}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            title="Close"
          >
            <X size={20} style={{ color: '#6b7280' }} />
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            padding: '16px 24px',
          }}
        >
          {isLoading && data.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 24px',
                color: '#6b7280',
              }}
            >
              <Loader2
                size={40}
                style={{
                  color: '#3b82f6',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <p style={{ marginTop: '16px', fontSize: '14px' }}>
                Loading detail data...
              </p>
              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}
              </style>
            </div>
          ) : error ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 24px',
                color: '#991b1b',
              }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#fef2f2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                }}
              >
                <span style={{ fontSize: '24px' }}>!</span>
              </div>
              <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                Failed to load data
              </p>
              <p style={{ fontSize: '14px', color: '#dc2626', textAlign: 'center' }}>
                {error}
              </p>
            </div>
          ) : data.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 24px',
                color: '#6b7280',
              }}
            >
              <p style={{ fontSize: '14px' }}>No data available</p>
            </div>
          ) : (
            <DataGrid
              columns={gridColumns}
              data={data}
              showToolbar={false}
              showPagination={totalPages > 1}
              manualPagination={true}
              pageCount={totalPages}
              totalCount={totalRows}
              pagination={pagination}
              onPaginationChange={handlePaginationChange}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              loading={isLoading}
              enableRowSelection={false}
              rowDensity="compact"
              fullHeight={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DrillToDetailModal;
